/**
 * A class which represents a Calendar overlay component.
 * This defines the structure and the main behavior of the component.
 *
 * @param conf A JSON object, which contains the following parameters:
 *      getDateFormat: a function which returns the date format string of value,
 *      i18n: the i18n JSON object,
 *      weekStart: the integer value of week starting day (0..6: Sunday  to Saturday)
 *      $input: input element
 *      $target: qtip target element
 */

import $ from 'jquery';
import * as common from '../../../shared/modules/common';
import { render } from 'mustache';
import moment from 'moment';
import * as util from './datePickerUtil';
import view from './datePickerView';

var DATE_FORMAT = 'YYYY-MM-DD',
    DEFAULT_START_DATE = moment('0000-01-01', DATE_FORMAT),
    DEFAULT_END_DATE = moment('9999-12-31', DATE_FORMAT),
    MONTH_LABEL_HEIGHT = 47,
    WEEK_LINE_HEIGHT = 44;

export default function Calendar(conf) {
    var $view,
        $main,
        $yearSelector,
        $yearInput,
        $prevYearButton,
        $nextYearButton,
        $monthSelectorView,
        $monthSelector,
        $daySelectorView,
        $daySelector,
        $footer,
        $legend,
        $doneButton,
        $todayButton,
        selectedDate,
        dateInfo,
        dateInfoToday,
        displayedYear,
        selectorHeight,
        isoWeekStart = util.convertWesternToIsoWeekday(conf.weekStart),
        startDate = DEFAULT_START_DATE,
        endDate = DEFAULT_END_DATE,
        hasBankHoliday,
        model = {
            'months': []
        };

    function isBankHoliday(targetDateInfo, date) {
        var bankHoliday = false,
            isoDateStr,
            enabledDay;
        if (targetDateInfo) {
            isoDateStr = date.format(DATE_FORMAT);
            enabledDay = $.inArray(isoDateStr, targetDateInfo.enabledDays) > -1;
            if (!enabledDay) {
                bankHoliday = $.inArray(isoDateStr, targetDateInfo.bankHolidays) > -1;
            }
        }
        return bankHoliday;
    }

    function getDateIndex(date) {
        /* eslint-disable no-bitwise */
        var yearIndex = date.year() << 9,
            monthIndex = date.month() << 5,
            dayIndex = date.date();
        return yearIndex + monthIndex + dayIndex;
        /* eslint-enable no-bitwise */
    }

    function isInDateRange(date, startDate, endDate) {
        var dateIndex = getDateIndex(date),
            startDateIndex = getDateIndex(startDate),
            endDateIndex = getDateIndex(endDate);
        return dateIndex >= startDateIndex && dateIndex <= endDateIndex;
    }

    function isDisabledDay(targetDateInfo, date) {
        var isoDateStr,
            selectableDay = isInDateRange(date, startDate, endDate),
            disabledDay = !selectableDay,
            enabledDay,
            isoWeekday,
            disabledWeekday;
        if (selectableDay && targetDateInfo) {
            isoDateStr = date.format(DATE_FORMAT);
            enabledDay = $.inArray(isoDateStr, targetDateInfo.enabledDays) > -1;
            if (!enabledDay) {
                disabledDay = $.inArray(isoDateStr, targetDateInfo.disabledDays) > -1;
                isoWeekday = util.convertIsoToWesternWeekday(date.isoWeekday());
                disabledWeekday = $.inArray(isoWeekday, targetDateInfo.disabledWeekdays) > -1;
                disabledDay = disabledDay || disabledWeekday;
            }
        }
        return disabledDay;
    }

    function getDateFormat() {
        var isValid = false,
            dateFormat = conf.getDateFormat();
        isValid = util.isUniqueSubstr(dateFormat, 'YYYY')
            && util.isUniqueSubstr(dateFormat, 'MM')
            && util.isUniqueSubstr(dateFormat, 'DD');
        dateFormat = isValid ? dateFormat : 'DD.MM.YYYY';
        return dateFormat;
    }

    function buildWeekday(week, date, dayOfWeekIndex) {
        var weekday = {
                'bankHoliday': isBankHoliday(dateInfo, date),
                'dayOfMonth': date.date(),
                'disabled': isDisabledDay(dateInfo, date),
                'hasTopBorder': week.weekOfMonth === 1,
                'value': date.format(getDateFormat())
            };
        week.weekdays[dayOfWeekIndex] = weekday;
        return weekday;
    }

    function buildWeek(month, date, weekOfMonthIndex) {
        var week = month.weeks[weekOfMonthIndex] || {
                'weekdays': []
            };
        month.weeks[weekOfMonthIndex] = week;
        return week;
    }

    /**
     * Calculates scroll top position of the month DOM element inside the scrollable day selector view.
     *
     * @param months array of month model objects
     * @param monthIndex month number (Jan..Dec: 1..12)
     *
     * @return Scroll top position of the month DOM element inside the scrollable day selector view.
     *      That's an integer, meaning scroll amount in pixels.
     */
    function calculateMonthScrollTop(months, monthIndex) {
        var scrollTop = 0,
            i;
        if (monthIndex > 0 && months && months.length >= monthIndex) {
            for (i = 0; i < monthIndex; i++) {
                scrollTop = scrollTop + MONTH_LABEL_HEIGHT + months[i].weeks.length * WEEK_LINE_HEIGHT;
            }
        }
        return scrollTop;
    }

    function buildMonth(months, index) {
        var month = months[index] || {
                'disabled': true,
                'month': conf.i18n.months[index],
                'monthShort': conf.i18n.monthsShort[index],
                'monthOfYear': index + 1,
                'monthOfYear2Digits': ('00' + (index + 1)).slice(-2),
                'scrollTop': calculateMonthScrollTop(months, index),
                'weeks': []
            };
        months[index] = month;
        return month;
    }

    function getModelWeekday(date) {
        var month,
            weekIndex,
            week,
            dayOfWeekIndex,
            weekday;
        if (model.year === date.year()) {
            month = model.months[date.month()];
            weekIndex = util.getWeekOfMonth(date, model.displayedYearOffset) - 1;
            week = month.weeks[weekIndex];
            dayOfWeekIndex = util.getDayOfWeek(date, isoWeekStart) - 1;
            weekday = week.weekdays[dayOfWeekIndex];
        }
        return weekday;
    }

    function updateModelToday() {
        var weekday = getModelWeekday(moment());
        if (weekday) {
            weekday.today = conf.i18n.today;
            weekday.todayAbbrev = conf.i18n.todayAbbrev;
        }
    }

    function updateModelSelectedDate() {
        var weekday = getModelWeekday(selectedDate);
        if (weekday) {
            weekday.selected = true;
        }
    }

    function buildModel() {
        var date = util.getFirstDayOfYear(displayedYear),
            month,
            week,
            weekday,
            monthIndex = 0,
            weekOfMonthIndex = 0,
            dayOfWeekIndex = util.getDayOfWeek(date, isoWeekStart) - 1;
        model = {
            'year': displayedYear,
            'displayedYearOffset': dayOfWeekIndex,
            'months': []
        };
        hasBankHoliday = false;
        do {
            month = buildMonth(model.months, date.month());
            week = buildWeek(month, date, weekOfMonthIndex);
            weekday = buildWeekday(week, date, dayOfWeekIndex);
            hasBankHoliday = hasBankHoliday || weekday.bankHoliday;
            month.disabled = weekday.disabled ? month.disabled : false;

            date.add(1, 'day');
            dayOfWeekIndex++;
            if (dayOfWeekIndex > 6) {
                dayOfWeekIndex = 0;
                weekOfMonthIndex++;
            }
            if (date.month() > monthIndex) {
                monthIndex++;
                weekOfMonthIndex = 0;
            }
        } while (date.year() === displayedYear);

        updateModelToday();
        updateModelSelectedDate();
    }

    function processDayEvent(event, task) {
        var $target = common.getEventTarget(event),
            $day = util.findBaseElemByClass($target, 'uwr-date-picker-day'),
            valid = $day ? $day.data('date') !== undefined : false,
            disabledDay = $day ? $day.hasClass('uwr-date-picker-day-disabled') : false,
            holiday = $day ? $day.hasClass('uwr-date-picker-day-bank-holiday') : false;
        if ($day && valid && !disabledDay && !holiday) {
            task($day);
        }
    }

    function daySelectHandler(event) {
        processDayEvent(event, function ($day) {
            selectDay($day);
            saveDateAndClose(event);
        });

    }

    function createDaySelector() {
        var $newDaySelector = $(render(view.daySelector, {
            'months': model.months
        }));
        $newDaySelector.on('click tap', daySelectHandler);
        $newDaySelector.height(selectorHeight);
        return $newDaySelector;
    }

    function updateDaySelector() {
        var $newDaySelector = createDaySelector();
        $daySelector.before($newDaySelector);
        $daySelector.remove();
        $daySelector = $newDaySelector;
    }

    function processMonthEvent(event, task) {
        loadDateInfo(function () {
            var $target = common.getEventTarget(event),
                $month = util.findBaseElemByClass($target, 'uwr-date-picker-month');
            if ($month && $month.data('month-of-year') !== undefined) {
                task($month);
            }
        });
    }

    function monthSelectHandler(event) {
        processMonthEvent(event, function ($month) {
            var monthOfYear = $month.data('month-of-year');
            updateDaySelectorView();
            updateDaySelector();
            showMonth(monthOfYear);
        });
        event.preventDefault();
    }

    function monthHoverInHandler(event) {
        processMonthEvent(event, function ($month) {
            $month.addClass('uwr-date-picker-month-hover');
        });
    }

    function monthHoverOutHandler(event) {
        processMonthEvent(event, function ($month) {
            $month.removeClass('uwr-date-picker-month-hover');
        });
    }

    function createMonthSelector() {
        var $newMonthSelector = $(render(view.monthSelector, {
            'months': model.months
        }));
        $newMonthSelector.find('.uwr-date-picker-month').each(function () {
            var $month = $(this);
            $month.on('click tap', monthSelectHandler);
            $month.hover(monthHoverInHandler, monthHoverOutHandler);
        });
        $newMonthSelector.height(selectorHeight);
        return $newMonthSelector;
    }

    function updateMonthSelector() {
        var $newMonthSelector = createMonthSelector();
        $monthSelector.before($newMonthSelector);
        $monthSelector.remove();
        $monthSelector = $newMonthSelector;
    }

    function showMonthSelector() {
        $monthSelectorView.show();
        $daySelectorView.hide();
    }

    function showDaySelector() {
        $daySelectorView.show();
        $monthSelectorView.hide();
    }

    function isTodaySelectable() {
        var now = moment();
        return isInDateRange(now, startDate, endDate) && !isDisabledDay(dateInfoToday, now)
            && !isBankHoliday(dateInfoToday, now);
    }

    function updateTodayButton() {
        var selectableDay = isTodaySelectable();
        if (selectableDay) {
            util.setButtonDisabled($todayButton, false);
        } else {
            util.setButtonDisabled($todayButton, true);
        }
    }

    function updatePrevYearButton() {
        if (displayedYear <= startDate.year()) {
            util.setButtonDisabled($prevYearButton, true);
        } else {
            util.setButtonDisabled($prevYearButton, false);
        }
    }

    function updateNextYearButton() {
        if (displayedYear >= endDate.year()) {
            util.setButtonDisabled($nextYearButton, true);
        } else {
            util.setButtonDisabled($nextYearButton, false);
        }
    }

    function getYear(yearString) {
        var year = parseInt(yearString, 10),
            startDateYear = startDate.year(),
            endDateYear = endDate.year();
        if (year.toString() !== yearString) {
            year = displayedYear;
        }
        if (year < startDateYear) {
            year = startDateYear;
        }
        if (year > endDateYear) {
            year = endDateYear;
        }
        return year;
    }

    function getDateInfo(year) {
        var args = [year, conf.$input[0]],
            // clone singleton JQuery 1.8 Promise object to prevent multiple asynchronous calls issue
            yearDateInfo = $.extend(true, {}, common.resolveConfig(conf.$input, 'date-info', args));
        yearDateInfo.markupAttr = common.data(conf.$input, 'date-info');
        return yearDateInfo;
    }

    function updateSelectableDateRange() {
        if (dateInfo && dateInfo.selectableDateRange) {
            startDate = util.parseDate(dateInfo.selectableDateRange.startDay, DATE_FORMAT, DEFAULT_START_DATE);
            endDate = util.parseDate(dateInfo.selectableDateRange.endDay, DATE_FORMAT, DEFAULT_END_DATE);
        }
    }

    function updateDateInfo() {
        var todayYear = moment().year();
        dateInfo = getDateInfo(displayedYear);
        if (displayedYear === todayYear) {
            dateInfoToday = dateInfo;
        } else {
            updateDateInfoToday();
        }
    }

    function updateDateInfoToday() {
        if (dateInfoToday === undefined) {
            dateInfoToday = getDateInfo(moment().year());
        }
    }

    function isPromise(obj) {
        return obj !== undefined && obj.promise !== undefined;
    }

    function updateButtons() {
        updatePrevYearButton();
        updateNextYearButton();
        updateTodayButton();
    }

    function loadDateInfo(processDateInfo) {
        var todayYear = moment().year();
        updateDateInfo();
        if (isPromise(dateInfo)) {
            updateSelectableDateRange();
            updateButtons();
            processDateInfo();
            dateInfo.done(function (data) {
                data.markupAttr = dateInfo.markupAttr;
                dateInfo = data;
                if (displayedYear === todayYear) {
                    dateInfoToday = dateInfo;
                } else {
                    loadDateInfoToday(updateTodayButton);
                }
                updateSelectableDateRange();
                updateButtons();
                processDateInfo();
            });
        } else {
            if (displayedYear === todayYear) {
                dateInfoToday = dateInfo;
            } else {
                loadDateInfoToday(updateTodayButton);
            }
            updateSelectableDateRange();
            updateButtons();
            processDateInfo();
        }
    }

    function loadDateInfoToday(processDateInfo) {
        updateDateInfoToday();
        if (isPromise(dateInfoToday)) {
            dateInfoToday.done(function (data) {
                dateInfoToday = data;
                processDateInfo();
            });
        } else {
            processDateInfo();
        }
    }

    function updateLegendText() {
        if (hasBankHoliday) {
            $legend.show();
        } else {
            $legend.hide();
        }
    }

    function selectDate(date) {
        var $day = findDay(date),
            monthOfYear = date.month() + 1;
        selectDay($day);
        showMonth(monthOfYear);
    }

    function updateDaySelectorView() {
        var yearChanged = displayedYear !== model.year,
            dateInfoAttrChanged = (dateInfo && dateInfo.markupAttr) !== common.data(conf.$input, 'date-info');
        if (yearChanged || dateInfoAttrChanged) {
            loadDateInfo(function () {
                buildModel();
                updateDaySelector();
                updateLegendText();
                updateButtons();
                selectDate(selectedDate);
            });
        } else {
            selectDate(selectedDate);
            $daySelector.height(selectorHeight);
        }
    }

    function updateMonthSelectorView() {
        if (displayedYear !== model.year) {
            loadDateInfo(function () {
                buildModel();
                updateMonthSelector();
                updateLegendText();
                updateButtons();
            });
        } else {
            $monthSelector.height(selectorHeight);
        }
    }

    function changeYear(yearString) {
        var year = getYear(yearString);
        setDisplayedYear(year);
        updateMonthSelectorView();
    }

    function yearHandler(newYear) {
        showMonthSelector();
        changeYear(newYear.toString());
        $yearInput.focus();
    }

    function prevYearHandler() {
        yearHandler(displayedYear - 1);
    }

    function nextYearHandler() {
        yearHandler(displayedYear + 1);
    }

    function showYearInputBorders() {
        $yearInput.removeClass('uwr-date-picker-displayed-year-input-label');
        $yearInput.addClass('uwr-date-picker-displayed-year-input-active uwr-year-input');
    }

    function hideYearInputBorders() {
        $yearInput.removeClass('uwr-date-picker-displayed-year-input-active uwr-year-input');
        $yearInput.addClass('uwr-date-picker-displayed-year-input-label');
    }

    function yearInputFocusHandler() {
        changeYear(displayedYear);
        showYearInputBorders();
        updateMonthSelector();
        showMonthSelector();
    }

    function yearInputBlurHandler() {
        var yearString = $yearInput.val(),
            year = getYear(yearString);
        setDisplayedYear(year);
        hideYearInputBorders();
    }

    function yearInputChangeHandler(event) {
        var ev = common.fixEvent(event),
            $target = common.getEventTarget(ev),
            yearString = $target.val();
        if (util.isOnlyNumberKey(ev) && yearString.length > 3 && !util.isTextSelected($target[0])) {
            // max. 4 digits
            ev.preventDefault();
        } else if (util.isDeleteKey(ev) && yearString.length < 1) {
            // min. 1 digit
            ev.preventDefault();
        } else if (util.isSetterKey(ev)) {
            if (yearString.length > 0) {
                yearHandler(yearString);
            } else {
                setDisplayedYear(displayedYear);
                ev.preventDefault();
            }
        }
    }

    function isNotEnabledKey(keyEvent) {
        return !util.isNavigatorKey(keyEvent) && !util.isSelectorKey(keyEvent)
            && !util.isModifierKey(keyEvent);
    }

    function yearInputKeydownHandler(event) {
        var ev = common.fixEvent(event),
            $target;
        if (util.isOnlyNumberKey(ev) || util.isDeleteKey(ev)) {
            yearInputChangeHandler(event);
        } else if (util.isExitKey(ev)) {
            $target = common.getEventTarget(ev);
            $target.val(displayedYear);
            hideYearInputBorders();
            conf.$input.focus();
        } else if (util.isSetterKey(ev)) {
            yearInputChangeHandler(event);
            hideYearInputBorders();
            conf.$input.focus();
            ev.preventDefault();
            return false;
        } else if (isNotEnabledKey(ev)) {
            return false;
        }
    }

    function findDay(date) {
        var dateString = moment(date, getDateFormat()).format(getDateFormat()),
            $day = $daySelector.find('[data-date="' + dateString + '"]');
        return $day;
    }

    function showMonth(monthOfYear) {
        var index = monthOfYear - 1,
            scrollTop;
        showDaySelector();
        scrollTop = model.months[index].scrollTop;
        $daySelector.scrollTop(scrollTop);
    }

    function saveDateAndClose(event) {
        var input = conf.$input[0],
            previousValue = input.value,
            newValue = selectedDate.format(getDateFormat());

        if (previousValue !== newValue) {
            input.value = newValue;
            common.dispatchEvent('change', input);
        }

        conf.$target.qtip('api').hide(event);
        hideYearInputBorders();
    }

    function updateDaySelection($day) {
        var $label = $day.next(),
            hasLabel = $label.hasClass('uwr-date-picker-day-label');
        $daySelector.find('.uwr-date-picker-day-selected').each(function () {
            var $selectedDay = $(this);
            $selectedDay.removeClass('uwr-date-picker-day-selected');
        });
        $day.addClass('uwr-date-picker-day-selected');
        if (hasLabel) {
            $label.addClass('uwr-date-picker-day-selected');
        }
    }

    function selectDay($day) {
        var selectedDateText;
        updateDaySelection($day);
        selectedDateText = $day.data('date');
        selectedDate = moment(selectedDateText, getDateFormat());
    }

    function buildYearSelector() {
        $yearSelector = $(render(view.yearSelector, {
            'displayedYear': displayedYear
        }));
        $prevYearButton = $yearSelector.find('.uwr-date-picker-prev-year');
        $prevYearButton.on('click tap', prevYearHandler);
        updatePrevYearButton();
        $nextYearButton = $yearSelector.find('.uwr-date-picker-next-year');
        $nextYearButton.on('click tap', nextYearHandler);
        updateNextYearButton();
        $yearInput = $yearSelector.find('input');
        $yearInput.on('focus', yearInputFocusHandler);
        $yearInput.on('blur', yearInputBlurHandler);
        $yearInput.on('keydown', yearInputKeydownHandler);
        $main.before($yearSelector);
    }

    function buildFooter() {
        $footer = $(render(view.footer, {
            'done': conf.i18n.done,
            'legendText': conf.i18n.legendText,
            'today': conf.i18n.today
        }));
        $legend = $footer.find('.uwr-date-picker-legend');
        $doneButton = $footer.find('.uwr-date-picker-done');
        $doneButton.on('click tap', function (event) {
            saveDateAndClose(event);
        });
        $todayButton = $footer.find('.uwr-date-picker-today');
        $todayButton.on('click tap', function (event) {
            var today = moment(),
                year = today.year(),
                $selectedDay;
            changeYear(year);
            $selectedDay = findDay(today);
            selectDay($selectedDay);
            selectedDate = today;
            saveDateAndClose(event);
        });
        $main.after($footer);
    }

    function buildView() {
        $view = $(render(view.calendar, {
            'selectMonth': conf.i18n.selectMonth,
            'weekdays': conf.i18n.weekdaysShort
        }));
        $view.on('click tap', changeYear);
        $main = $view.find('.uwr-date-picker-calendar-main');
        $monthSelectorView = $view.find('.uwr-date-picker-month-selector-view');
        $monthSelector = $view.find('.uwr-date-picker-month-selector');
        $daySelectorView = $view.find('.uwr-date-picker-day-selector-view');
        $daySelector = $view.find('.uwr-date-picker-day-selector');
        buildYearSelector();
        buildFooter();
    }

    function setDisplayedYear(year) {
        if (year !== displayedYear) {
            dateInfo = undefined;
            dateInfoToday = undefined;
            displayedYear = parseInt(year, 10);
        }

        if ($yearInput && $yearInput.val() !== displayedYear.toString()) {
            $yearInput.val(displayedYear);
        }
    }

    function init() {
        selectedDate = util.parseDate(conf.$input.val(), getDateFormat());
        setDisplayedYear(selectedDate.year());
        buildView();
    }

    function showDate() {
        hideYearInputBorders();
        showDaySelector();
        selectedDate = util.parseDate(conf.$input.val(), getDateFormat());
        setDisplayedYear(selectedDate.year());
        updateDaySelectorView();
        showMonth(selectedDate.month() + 1);
    }

    this.showDate = function () {
        showDate();
    };

    this.showView = function () {
        if ($daySelectorView.is(':visible')) {
            showDate();
        } else {
            updateMonthSelector();
            showMonthSelector();
        }
    };

    this.updateView = function () {
        init();
    };

    this.setSelectorHeight = function (newHeight) {
        selectorHeight = newHeight;
    };

    this.getView = function () {
        if ($view === undefined) {
            init();
        }
        return $view;
    };
}



//////////////////
// WEBPACK FOOTER
// ./widgets/DatePicker/js/datePickerCalendar.js
// module id = 83
// module chunks = main